import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, MapPin, Calendar, Users, Download, MessageCircle, Mail, Phone, Globe, Instagram, Facebook, Youtube, Linkedin, Twitter } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicQuotation() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const trackedOnce = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/quotations/${token}`);
        setData(data);
      } catch { setErr(true); }
    })();
  }, [token]);

  const downloadPdf = () => {
    window.open(`${BACKEND}/api/public/quotations/${token}/pdf`, "_blank");
  };
  const shareWA = () => {
    const url = window.location.href;
    const text = encodeURIComponent(`Check out this travel quotation: ${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500">This quotation link is invalid or expired.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const { quotation: q, agency } = data;
  const dest = q.destination || {};
  const social = agency.social || {};

  return (
    <div className="min-h-screen bg-[#FCFCFC] text-slate-900">
      {/* HERO */}
      <div className="relative h-[70vh] max-h-[560px] w-full overflow-hidden">
        {dest.hero_image ? <img src={dest.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-[#0F1E5B]" />}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/85" />
        {/* header bar */}
        <div className="relative z-10 flex items-center justify-between p-5 lg:p-8">
          <div className="flex items-center gap-3">
            {agency.logo_url ? (
              <img src={agency.logo_url} alt="" className="h-10 w-auto bg-white/90 p-1.5 rounded" />
            ) : (
              <div className="w-10 h-10 bg-white/10 backdrop-blur border border-white/30 rounded flex items-center justify-center"><Sparkles size={16} className="text-[#BBF7D0]" /></div>
            )}            <div className="text-white">
              <div className="font-editorial text-lg leading-none">{agency.agency_name || "Your Travel Agency"}</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#BBF7D0] mt-1">Curated Journey</div>
            </div>
          </div>
        </div>
        {/* Title */}
        <div className="absolute bottom-8 left-6 lg:left-14 right-6 z-10 text-white max-w-3xl">
          <div className="overline !text-[#BBF7D0]">Prepared for {q.lead?.name || "Valued Traveler"}</div>
          <h1 className="font-editorial text-4xl lg:text-6xl mt-3 leading-[1.05]">{q.package_title || `${dest.country || ""} Journey`}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-5 text-sm">
            <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {dest.country || "—"}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {q.start_date || "—"} → {q.end_date || "—"}</span>
            <span className="inline-flex items-center gap-1.5"><Users size={14} /> {q.lead?.adults || 0} Adults, {q.lead?.children || 0} Children</span>
            <span className="inline-flex items-center gap-1.5 bg-[#16A34A] px-2.5 py-1 rounded text-xs font-semibold">{q.total_nights}N / {q.total_days}D</span>
          </div>
        </div>
      </div>

      {/* Actions bar */}
      <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-border z-40 py-3">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-slate-500 font-mono-e">{q.quotation_number}</div>
          <div className="flex items-center gap-2">
            <button data-testid="public-share-wa" onClick={shareWA} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3.5 py-2 border border-border rounded hover:border-[#16A34A]"><MessageCircle size={13}/> Share</button>
            <button data-testid="public-download-btn" onClick={downloadPdf} className="btn-bronze inline-flex items-center gap-2 text-sm"><Download size={14}/> Download PDF</button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-14 space-y-14">

        {dest.overview && (
          <Section overline="The Destination" title={dest.country}>
            <p className="text-slate-700 leading-relaxed">{stripHtml(dest.overview)}</p>
            <div className="grid sm:grid-cols-3 gap-4 mt-6 text-sm">
              {dest.best_time && <MiniStat k="Best Time" v={dest.best_time} />}
              {dest.currency && <MiniStat k="Currency" v={dest.currency} />}
              {dest.weather && <MiniStat k="Weather" v={dest.weather} />}
            </div>
            {dest.attractions && (
              <div className="mt-6"><div className="overline text-[9px] mb-2">Famous Attractions</div>
                <p className="text-sm text-slate-700 leading-relaxed">{stripHtml(dest.attractions)}</p></div>
            )}
          </Section>
        )}

        <Section overline="Tour Summary" title="At a Glance">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <Row k="Lead Passenger" v={q.lead?.name} />
            <Row k="Travelers" v={`${q.lead?.adults || 0} Adults, ${q.lead?.children || 0} Children`} />
            <Row k="Duration" v={`${q.total_nights}N / ${q.total_days}D`} />
            <Row k="Dates" v={`${q.start_date} → ${q.end_date}`} />
          </div>
          {q.cities?.length > 0 && (
            <div className="mt-5">
              <div className="overline text-[9px] mb-2">Cities Covered</div>
              <div className="flex flex-wrap gap-2">
                {q.cities.map((c, i) => (
                  <span key={i} className="px-3 py-1 bg-[#BBF7D0]/60 border border-[#BBF7D0] rounded text-xs">{c.city} <b>({c.nights}N)</b></span>
                ))}
              </div>
            </div>
          )}
        </Section>

        {q.flights?.length > 0 && (
          <Section overline="Flights" title="Air Travel">
            <div className="space-y-3">
              {q.flights.map((f, i) => (
                <div key={i} className="border border-border rounded p-4 text-sm">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <b>{f.airline}</b>
                    <span className="text-slate-500">{f.flight_number}</span>
                    <span className="text-[#16A34A] font-mono-e text-xs">{f.cabin}</span>
                  </div>
                  <div className="mt-2 grid sm:grid-cols-2 gap-2 text-slate-700">
                    <div>{f.dep_airport} → {f.arr_airport}</div>
                    <div className="text-xs text-slate-500">{f.dep_datetime} — {f.arr_datetime}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {q.hotels?.length > 0 && (
          <Section overline="Accommodations" title="Hotels">
            <div className="grid sm:grid-cols-2 gap-4">
              {q.hotels.map((h, i) => (
                <div key={i} className="border border-border rounded overflow-hidden">
                  {h.image_url && <img src={h.image_url} alt="" className="w-full h-40 object-cover" />}
                  <div className="p-4">
                    <div className="font-editorial text-lg leading-tight">{h.name}</div>
                    <div className="text-xs text-slate-500 mt-1">{h.address}</div>
                    <div className="mt-3 flex items-center gap-3 text-xs text-slate-700">
                      <span>{h.check_in} → {h.check_out}</span>
                      <span className="text-[#16A34A] font-semibold">{h.nights}N</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{h.room_category} · {h.meal_plan}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {q.itinerary?.length > 0 && (
          <Section overline="Day-Wise Itinerary" title="Your Journey">
            <div className="space-y-4">
              {q.itinerary.map((d, i) => (
                <div key={i} className="border-l-2 border-[#16A34A] pl-5">
                  <div className="flex items-baseline gap-3">
                    <span className="text-xs bg-[#0F1E5B] text-white px-2 py-0.5 rounded font-mono-e">DAY {d.day}</span>
                    <span className="text-xs text-[#16A34A] font-semibold">{d.date}</span>
                    <span className="text-xs text-slate-500">{d.city}</span>
                  </div>
                  <div className="mt-2 font-editorial text-xl">{d.title}</div>
                  {d.description && <p className="mt-1 text-sm text-slate-700 leading-relaxed">{stripHtml(d.description)}</p>}
                  <div className="mt-2 text-xs text-slate-500 space-x-3">
                    {d.meal_plan && <span>🍽 {d.meal_plan}</span>}
                    {d.sightseeing && <span>· {d.sightseeing}</span>}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {(q.inclusions?.length > 0 || q.exclusions?.length > 0) && (
          <div className="grid sm:grid-cols-2 gap-4">
            {q.inclusions?.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-5">
                <div className="overline text-emerald-700 !text-[10px]">Inclusions</div>
                <ul className="mt-3 space-y-1.5 text-sm text-slate-800">{q.inclusions.map((x, i) => <li key={i}>✓ {x}</li>)}</ul>
              </div>
            )}
            {q.exclusions?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded p-5">
                <div className="overline text-red-700 !text-[10px]">Exclusions</div>
                <ul className="mt-3 space-y-1.5 text-sm text-slate-800">{q.exclusions.map((x, i) => <li key={i}>✕ {x}</li>)}</ul>
              </div>
            )}
          </div>
        )}

        {q.pricing && (
          <Section overline="Investment" title="Pricing Summary">
            <div className="border border-border rounded overflow-hidden">
              <div className="divide-y divide-border text-sm">
                {[["Package Cost","package_cost"],["Taxes","taxes"],["TCS","tcs"],["Visa Charges","visa_charges"],["Markup","markup"],["Service Charges","service_charges"],["Discount","discount"]].filter(([_,k]) => q.pricing[k]).map(([l,k]) => (
                  <div key={k} className="flex justify-between px-5 py-2.5">
                    <span className="text-slate-600">{l}</span>
                    <span>{q.pricing.currency} {(k==="discount"?"-":"")}{Number(q.pricing[k]).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="bg-[#16A34A] text-white px-5 py-4 flex justify-between items-baseline">
                <div className="overline !text-[#BBF7D0]">Grand Total</div>
                <div className="font-editorial text-3xl">{q.pricing.currency} {Number(q.pricing.grand_total).toLocaleString()}</div>
              </div>
            </div>
          </Section>
        )}

        {(q.policies?.payment_policy || q.policies?.cancellation_policy || q.policies?.terms_conditions) && (
          <Section overline="Policies" title="The Fine Print">
            {q.policies?.payment_policy && <PolicyBlock label="Payment Policy" html={q.policies.payment_policy} />}
            {q.policies?.cancellation_policy && <PolicyBlock label="Cancellation Policy" html={q.policies.cancellation_policy} />}
            {q.policies?.terms_conditions && <PolicyBlock label="Terms & Conditions" html={q.policies.terms_conditions} />}
          </Section>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-[#0F1E5B] text-white noise-bg py-14">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-2 gap-10">
          <div>
            <div className="font-editorial text-2xl">{agency.agency_name || "Traveloble"}</div>
            {agency.about_us && <p className="mt-3 text-sm text-white/70 leading-relaxed">{stripHtml(agency.about_us).slice(0, 220)}…</p>}
          </div>
          <div className="space-y-2 text-sm">
            {agency.address && <div className="flex items-start gap-2 text-white/80"><MapPin size={13} className="mt-1 shrink-0"/> {agency.address}</div>}
            {agency.contact_numbers && <div className="flex items-center gap-2 text-white/80"><Phone size={13}/> {agency.contact_numbers}</div>}
            {agency.email && <div className="flex items-center gap-2 text-white/80"><Mail size={13}/> {agency.email}</div>}
            {agency.website && <div className="flex items-center gap-2 text-white/80"><Globe size={13}/> {agency.website}</div>}
            <div className="flex items-center gap-3 pt-2">
              {social.facebook && <a href={social.facebook} className="text-[#BBF7D0]"><Facebook size={16}/></a>}
              {social.instagram && <a href={social.instagram} className="text-[#BBF7D0]"><Instagram size={16}/></a>}
              {social.youtube && <a href={social.youtube} className="text-[#BBF7D0]"><Youtube size={16}/></a>}
              {social.linkedin && <a href={social.linkedin} className="text-[#BBF7D0]"><Linkedin size={16}/></a>}
              {social.twitter && <a href={social.twitter} className="text-[#BBF7D0]"><Twitter size={16}/></a>}
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 mt-10 pt-6 border-t border-white/10 flex justify-between items-center text-xs text-white/50">
          <div>© 2026 · Curated with care</div>
          <div className="text-[#BBF7D0] italic">Powered by Traveloble</div>
        </div>
      </footer>
    </div>
  );
}

function Section({ overline, title, children }) {
  return (
    <section>
      <div className="overline">{overline}</div>
      <h2 className="font-editorial text-3xl mt-2 mb-5 text-slate-900">{title}</h2>
      {children}
    </section>
  );
}
function MiniStat({ k, v }) {
  return (<div className="bg-slate-50 border border-border rounded p-3"><div className="overline text-[9px]">{k}</div><div className="mt-1">{v}</div></div>);
}
function Row({ k, v }) {
  return (<div className="flex justify-between border-b border-border py-2"><span className="text-slate-500">{k}</span><span className="text-slate-800">{v || "—"}</span></div>);
}
function PolicyBlock({ label, html }) {
  return (
    <div className="mb-5">
      <div className="overline text-[9px] mb-2">{label}</div>
      <div className="text-sm text-slate-700 leading-relaxed prose-quotation" dangerouslySetInnerHTML={{__html: html}} />
    </div>
  );
}
function stripHtml(s) { return (s || "").replace(/<[^>]+>/g, ""); }
